// client/src/components/pages/LexiStar/DispatchBoard/DispatchBoard.tsx (ST-00250)
//
// The dispatch planning board: route /lexistar/dispatch-board, sidebar LexiStar · Operate →
// Dispatch Board (next to Plan & Dispatch). Page permission lexistar.trips.view
// (config/pagePermissions); adding orders, allocating and releasing need lexistar.trips.manage.
//
//   Day + shift    ◀ day ▶ on the shared date picker, All / Day / Night; both live in the URL
//   Rail           the organisation's trucks (TruckRail): available first, workshop / breakdown after
//   Cards          one per order (OrderCard): reference, customer, material, lane, the absorption
//                  ring (under / on / over the target in tonnes or loads), the trucks on it
//   Allocate       drag a truck onto a card (or a chip from one card to another), tap-select then
//                  "Allocate here", or Enter on a truck → "Allocate to order…" menu; the server
//                  creates a Planned trip on the order's lane (Plan & Dispatch dispatches it)
//   Release        the cross on a chip, Delete on it, or drop the chip back on the rail
//   Undo           the last allocation / release, for 15 s
//   Two planners   409 stale → one sentence and a refresh; DISPATCH_BOARD.CHANGED refreshes live
// Phone / tablet: the rail sits above the cards (two columns of trucks) and scrolls; from xl it is
// a sticky left column. Cards fill the width at 280 px or more each.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaPlus, FaSyncAlt, FaThLarge } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import { SegmentedControl, useAuth } from '../../../../types/commonComponents';
import type { BoardAllocation, BoardOrder, BoardTruck, DispatchShift, OrderInput, OrderPatch } from '../../../../types/lexistar-dispatch-board';
import { ConnectionIndicator, EmptyState, ErrorBanner, LoadingState, NoPermissionPage, NoticeBanner, PageHeader } from '../StatusBoard/atoms';
import usePlanReference from '../PlanDispatch/usePlanReference';
import { conflictOf, errorText } from '../../../../services/lexistarDispatchBoard';
import useDispatchBoard from './useDispatchBoard';
import OrderCard from './OrderCard';
import TruckRail from './TruckRail';
import OrderDialog from './OrderDialog';
import { STATE_META, dayLabel, dayString, isDay, parseDay, shiftDay, todayString } from './boardLogic';

export const VIEW = 'lexistar.trips.view';
export const MANAGE = 'lexistar.trips.manage';

type ShiftFilter = DispatchShift | 'all';
const SHIFT_OPTIONS: Array<{ value: ShiftFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
];
const isShift = (s: string | null): s is ShiftFilter => s === 'all' || s === 'day' || s === 'night';

const stepButton = 'touch-target p-2 rounded-md border border-slate-300 bg-surface text-slate-600 hover:bg-slate-50';

export default function DispatchBoard() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(MANAGE);
  const canView = canManage || hasPermission(VIEW);
  const [params, setParams] = useSearchParams();
  const date = isDay(params.get('date')) ? (params.get('date') as string) : todayString();
  const shift: ShiftFilter = isShift(params.get('shift')) ? (params.get('shift') as ShiftFilter) : 'all';
  const setParam = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '' || (k === 'date' && v === todayString()) || (k === 'shift' && v === 'all')) next.delete(k);
        else next.set(k, v);
      }
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const board = useDispatchBoard({ date, shift, enabled: canView });
  const ref = usePlanReference({ enabled: canManage });
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; order: BoardOrder | null }>({ open: false, order: null });
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const trucksById = useMemo(() => new Map((board.data?.trucks ?? []).map((t) => [t.asset_id, t])), [board.data]);
  const orders = useMemo(() => board.data?.orders ?? [], [board.data]);
  const selectedTruck = selectedAssetId ? (trucksById.get(selectedAssetId) ?? null) : null;

  useEffect(() => {
    if (selectedAssetId && !trucksById.has(selectedAssetId)) setSelectedAssetId(null);
  }, [selectedAssetId, trucksById]);

  const allocateTo = useCallback(
    async (order: BoardOrder, assetId: string) => {
      const truck = trucksById.get(assetId);
      if (!truck) return;
      setSelectedAssetId(null);
      await board.allocateTruck(order, truck);
    },
    [board, trucksById]
  );
  const onDropAllocation = useCallback(
    async (to: BoardOrder, fromOrderId: string, allocationId: string) => {
      const from = orders.find((o) => o.id === fromOrderId);
      const allocation = from?.allocations.find((a) => a.id === allocationId);
      if (!from || !allocation) return;
      await board.moveAllocation(from, allocation, to);
    },
    [board, orders]
  );
  const onReleaseDrop = useCallback(
    async (orderId: string, allocationId: string) => {
      const order = orders.find((o) => o.id === orderId);
      const allocation = order?.allocations.find((a) => a.id === allocationId);
      if (!order || !allocation) return;
      await board.releaseAllocation(order, allocation);
    },
    [board, orders]
  );
  const onRelease = useCallback((order: BoardOrder, allocation: BoardAllocation) => board.releaseAllocation(order, allocation), [board]);
  const onAllocateFromRail = useCallback((truck: BoardTruck, order: BoardOrder) => allocateTo(order, truck.asset_id), [allocateTo]);

  const openDialog = (order: BoardOrder | null) => {
    setDialogError(null);
    setDialog({ open: true, order });
  };
  const closeDialog = () => setDialog({ open: false, order: null });
  const saveWith = async (fn: () => Promise<unknown>) => {
    setSaving(true);
    setDialogError(null);
    try {
      await fn();
      closeDialog();
    } catch (err) {
      const conflict = conflictOf(err);
      setDialogError(
        conflict?.code === 'stale'
          ? 'Another planner changed this order meanwhile — the board has been refreshed. Close and open the order again.'
          : errorText(err, 'Could not save the order')
      );
    } finally {
      setSaving(false);
    }
  };
  const onCreate = (input: OrderInput) => saveWith(() => board.createOrder(input));
  const onUpdate = (id: string, patch: OrderPatch) => saveWith(() => board.updateOrder(id, patch));

  const dayPicker = (
    <div className="flex items-center gap-1.5 w-full sm:w-auto" role="group" aria-label="Day">
      <button type="button" onClick={() => setParam({ date: shiftDay(date, -1) })} aria-label="Previous day" className={stepButton}>
        <FaChevronLeft size={11} />
      </button>
      <div className="flex-1 min-w-0 sm:flex-none sm:w-52">
        <UnifiedDateRangePicker
          startDate={parseDay(date)}
          endDate={parseDay(date)}
          onStartDateChange={(d) => d && setParam({ date: dayString(d) })}
          onEndDateChange={() => {}}
          bounds="all"
          mode="day"
        />
      </div>
      <button type="button" onClick={() => setParam({ date: shiftDay(date, 1) })} aria-label="Next day" className={stepButton}>
        <FaChevronRight size={11} />
      </button>
      {date !== todayString() && (
        <button type="button" onClick={() => setParam({ date: null })} className="touch-target px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-surface text-slate-700 hover:bg-slate-50">
          Today
        </button>
      )}
    </div>
  );

  const header = (
    <PageHeader
      icon={FaThLarge}
      title="Dispatch Board"
      subtitle={`${dayLabel(date)} — drag trucks onto the day's orders; each truck becomes a planned trip to dispatch on Plan & Dispatch.`}
      // The day stepper and the shift control need their own row (PageHeader's stacked layout).
      inline={false}
      toolbar={
        canView ? (
          <div className="flex flex-wrap items-center gap-2">
            {dayPicker}
            <SegmentedControl options={SHIFT_OPTIONS} value={shift} onChange={(v) => setParam({ shift: v })} ariaLabel="Shift" />
          </div>
        ) : null
      }
      actions={
        canView ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => board.reload()} aria-label="Refresh" title="Refresh" className={stepButton}>
              <FaSyncAlt size={11} />
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => openDialog(null)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm"
              >
                <FaPlus size={11} /> New order
              </button>
            )}
            <ConnectionIndicator status={board.connection} compact />
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return <NoPermissionPage header={header} message="You do not have permission to see the dispatch board." />;
  }

  const legend = (
    <ul className="flex flex-wrap gap-2 text-[11px]" aria-label="Absorption legend">
      {(['under', 'on', 'over'] as const).map((s) => (
        <li key={s} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${STATE_META[s].pill}`}>
          <span aria-hidden="true">{STATE_META[s].glyph}</span>
          {STATE_META[s].word}
        </li>
      ))}
      <li className="text-slate-500 self-center">On target = within half a load.</li>
    </ul>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-6 space-y-4">
        <div>
          <ErrorBanner message={board.error} onDismiss={() => board.setError(null)} />
          <NoticeBanner
            message={
              board.notice ? (
                <span className="inline-flex flex-wrap items-center gap-3">
                  <span>{board.notice}</span>
                  {board.undo && (
                    <button type="button" onClick={() => board.runUndo()} className="font-semibold underline underline-offset-2 hover:no-underline">
                      {board.undo.label}
                    </button>
                  )}
                </span>
              ) : null
            }
            onDismiss={() => board.setNotice(null)}
          />
        </div>

        {board.loading && !board.data ? (
          <LoadingState label="Loading the dispatch board..." />
        ) : (
          <div className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)] items-start">
            <div className="xl:sticky xl:top-4 max-h-[60vh] xl:max-h-[calc(100vh-7rem)] overflow-auto">
              <TruckRail
                trucks={board.data?.trucks ?? []}
                orders={orders}
                canManage={canManage}
                selectedAssetId={selectedAssetId}
                onSelect={setSelectedAssetId}
                onAllocate={onAllocateFromRail}
                onReleaseDrop={onReleaseDrop}
                busy={board.busyOrderIds.size > 0}
              />
            </div>
            <section aria-label="Orders" className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Orders <span className="text-slate-400">({orders.length})</span>
                </h2>
                {legend}
              </div>
              {orders.length === 0 ? (
                <EmptyState icon={FaThLarge} title="No orders on this board yet">
                  <p>{canManage ? 'Add the day’s orders, then drag trucks onto them.' : 'Nothing has been planned for this day and shift.'}</p>
                  {canManage && (
                    <button type="button" onClick={() => openDialog(null)} className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm">
                      <FaPlus size={11} /> New order
                    </button>
                  )}
                </EmptyState>
              ) : (
                <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
                  {orders.map((o) => (
                    <OrderCard
                      key={o.id}
                      order={o}
                      canManage={canManage}
                      busy={board.busyOrderIds.has(o.id)}
                      selectedTruck={selectedTruck}
                      onDropTruck={allocateTo}
                      onDropAllocation={onDropAllocation}
                      onRelease={onRelease}
                      onEdit={openDialog}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      <OrderDialog
        isOpen={dialog.open}
        order={dialog.order}
        date={date}
        defaultShift={shift === 'all' ? null : shift}
        lanes={ref.lanes}
        routes={ref.routes}
        saving={saving}
        error={dialogError}
        onClose={closeDialog}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />
    </div>
  );
}
